import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

// Shared logged-out gate: a blurred preview of the app shell behind a sign in /
// sign up CTA. Used to protect account surfaces (dashboard, settings, etc.) so
// logged-out visitors get a clear call to action instead of seeing the content.
// Responsive: the CTA card is centered and capped at max-w-md, and pt-32 clears
// the fixed navbar on mobile, tablet, and desktop.
export function SignInGate({
  title = 'Sign in to access your account',
  description = 'Sign in to view this page, or create a free account to get started.',
}: {
  title?: string;
  description?: string;
}) {
  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />
      <main className="container mx-auto px-6 lg:px-12 pt-32 pb-16">
        <div className="relative">
          {/* Blurred app-shell preview behind the CTA. */}
          <div className="pointer-events-none select-none blur-[6px] opacity-70" aria-hidden="true">
            <div className="h-8 w-64 bg-slate-200 rounded mb-8" />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6">
                  <div className="h-4 w-28 bg-slate-200 rounded mb-4" />
                  <div className="h-3 w-3/4 bg-slate-100 rounded mb-2" />
                  <div className="h-3 w-1/2 bg-slate-100 rounded" />
                </div>
              ))}
            </div>
          </div>
          {/* CTA overlay. */}
          <div className="absolute inset-0 flex items-start justify-center pt-12 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-100 p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-pantheon-teal/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-pantheon-teal" />
              </div>
              <h2 className="text-2xl font-display font-black text-navy-900 mb-2">{title}</h2>
              <p className="text-muted-foreground mb-6">{description}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/auth?mode=signin" className="flex-1">
                  <Button className="w-full bg-pantheon-teal hover:bg-pantheon-teal-dark text-white rounded-xl h-12 font-bold">
                    Sign In
                  </Button>
                </Link>
                <Link to="/auth?mode=signup" className="flex-1">
                  <Button variant="outline" className="w-full rounded-xl h-12 font-bold">
                    Create Free Account
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// Route wrapper: renders children only when logged in; otherwise the gate. The
// protected page never mounts while logged out, so its data fetches do not run.
export function RequireAuth({
  children,
  title,
  description,
}: {
  children: ReactNode;
  title?: string;
  description?: string;
}) {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn) return <SignInGate title={title} description={description} />;
  return <>{children}</>;
}

export default RequireAuth;
